import type { Theme } from '../hooks/useTheme';
import { ThemeToggleButton, PaletteSwatches } from './ui/ThemeControls';
import { useAuth } from '../contexts/AuthContext';
import type { PlanType } from '../../shared/types';
import NotificationBell from './NotificationBell';
import type { AppNotification } from '../hooks/useAnalysisJobs';

interface HeaderProps {
  theme: Theme;
  onToggleTheme: () => void;
  onMenuToggle?: () => void;
  /** Nombre d'analyses en cours / en attente — alimente le pill et la cloche. */
  runningCount?: number;
  queuedCount?: number;
  /** Clic sur le pill — repositionne l'app sur l'analyse en cours la plus récente. */
  onResumeLoader?: () => void;
  /** Centre de notifications. */
  notifications?: AppNotification[];
  unreadCount?: number;
  onOpenNotification?: (notif: AppNotification) => void;
  onMarkAllNotificationsRead?: () => void;
  onDismissNotification?: (id: string) => void;
  /** Pendant l'onboarding, seul point d'accès au compte (sidebar masquée).
   *  Sans lui, un échec silencieux (ex. code promo non appliqué) laisse
   *  l'utilisateur sans aucun moyen d'atteindre Facturation ou de supprimer
   *  son compte. */
  onAccountClick?: () => void;
}

const PREVIEW_OPTIONS: { value: PlanType | 'admin' | 'onboarding' | 'bundle'; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'onboarding', label: 'Signup' },
  { value: 'bundle', label: 'Bundle' },
  { value: 'beta', label: 'Beta' },
  { value: 'pro', label: 'Pro' },
  { value: 'premium', label: 'Premium' },
  { value: 'partner', label: 'Partenaire' },
];

export default function Header({
  theme,
  onToggleTheme,
  onMenuToggle,
  runningCount = 0,
  queuedCount = 0,
  onResumeLoader,
  notifications = [],
  unreadCount = 0,
  onOpenNotification,
  onMarkAllNotificationsRead,
  onDismissNotification,
  onAccountClick,
}: HeaderProps) {
  const { user, previewPlan, setPreviewPlan, previewOnboarding, setPreviewOnboarding, previewBundle, setPreviewBundle, isOnboarding, signOut } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin === true;
  const inFlight = runningCount + queuedCount;
  const identity = user?.displayName || user?.email || '';

  return (
    <header className="border-b border-slate-200 dark:border-slate-800 shader:border-white/10 pastel:border-b-0 bg-white/80 dark:bg-slate-900/80 shader:bg-white/5 pastel:bg-transparent shader:backdrop-blur-xl backdrop-blur-sm pastel:backdrop-blur-none sticky top-0 z-50">
      <div className="px-4 h-14 flex-shrink-0 flex items-center justify-between gap-3">
        {/* Left: (mobile hamburger) + persistent Bêta · 100 places badge */}
        <div className="flex items-center gap-2 min-w-0">
          {onMenuToggle && !isOnboarding && (
            <button
              onClick={onMenuToggle}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10 text-slate-500 dark:text-slate-400 shader:text-white/70 transition-colors"
              aria-label="Ouvrir le menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          <span
            className="shrink-0 text-xs px-2.5 py-1 pastel:px-3 pastel:py-1.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 shader:bg-violet-500/20 shader:text-violet-200 shader:border shader:border-violet-400/20 pastel:bg-(--p-a) pastel:text-(--p-ink) font-medium cursor-default"
            title="Version bêta — limitée à 100 places"
          >
            Bêta · 100 places
          </span>
        </div>

        {/* Center: in-progress analyses pill — cliquer ramène sur l'analyse en cours la plus récente. */}
        {!isOnboarding && inFlight > 0 && (
          <button
            onClick={onResumeLoader}
            className="flex items-center gap-2 px-3 py-1 pastel:px-3.5 pastel:py-1.5 rounded-full bg-violet-50 dark:bg-violet-900/20 shader:bg-violet-500/10 pastel:bg-(--p-b) border border-violet-200 dark:border-violet-700/50 shader:border-violet-400/30 pastel:border-transparent text-violet-700 dark:text-violet-300 shader:text-violet-200 pastel:text-(--p-ink) text-xs font-medium hover:bg-violet-100 dark:hover:bg-violet-900/30 shader:hover:bg-violet-500/20 pastel:hover:bg-(--p-b) transition-colors"
            title="Cliquez pour revenir à l'analyse en cours"
            aria-label={`${inFlight} analyse(s) en cours`}
          >
            <svg className="w-3.5 h-3.5 animate-spin shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" d="M12 3a9 9 0 0 1 9 9" />
            </svg>
            <span className="whitespace-nowrap">
              {inFlight > 1 ? `${inFlight} analyses en cours` : 'Analyse en cours'}
            </span>
          </button>
        )}

        {/* Right: preview toggle + badge + theme */}
        <div className="flex items-center gap-2">
          {/* Super admin plan preview toggle */}
          {isSuperAdmin && (
            <div className="flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-900/20 shader:bg-amber-500/10 border border-amber-200 dark:border-amber-800 shader:border-amber-400/20 px-1 py-0.5">
              {PREVIEW_OPTIONS.map(opt => {
                const isActive = opt.value === 'admin'
                  ? previewPlan === null && !previewOnboarding && !previewBundle
                  : opt.value === 'onboarding'
                    ? previewOnboarding
                    : opt.value === 'bundle'
                      ? previewBundle
                      : previewPlan === opt.value && !previewOnboarding && !previewBundle;
                return (
                  <button
                    key={opt.value}
                    onClick={() => {
                      if (opt.value === 'admin') {
                        setPreviewPlan(null);
                        setPreviewOnboarding(false);
                        setPreviewBundle(false);
                      } else if (opt.value === 'onboarding') {
                        setPreviewOnboarding(true);
                      } else if (opt.value === 'bundle') {
                        setPreviewBundle(true);
                      } else {
                        setPreviewPlan(opt.value as PlanType);
                      }
                    }}
                    className={`px-2 py-0.5 text-[10px] font-medium rounded-full transition-colors ${
                      isActive
                        ? 'bg-amber-500 text-white'
                        : 'text-amber-700 dark:text-amber-300 shader:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-800/30 shader:hover:bg-amber-500/20'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Onboarding : pas de cloche, juste identité + déconnexion (focus sur le centre). */}
          {isOnboarding ? (
            <>
              <span className="hidden sm:block max-w-[180px] truncate text-xs text-slate-500 dark:text-slate-400 shader:text-white/70" title={identity}>
                {identity}
              </span>
              {onAccountClick && (
                <button
                  onClick={onAccountClick}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300 shader:text-white/80 hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors"
                  title="Mon compte"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                  </svg>
                  <span className="hidden sm:inline">Mon compte</span>
                </button>
              )}
              <button
                onClick={() => signOut()}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300 shader:text-white/80 hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors"
                title="Se déconnecter"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span className="hidden sm:inline">Se déconnecter</span>
              </button>
            </>
          ) : (
            <NotificationBell
              notifications={notifications}
              unreadCount={unreadCount}
              runningCount={runningCount}
              queuedCount={queuedCount}
              onOpen={(n) => onOpenNotification?.(n)}
              onMarkAllRead={() => onMarkAllNotificationsRead?.()}
              onDismiss={(id) => onDismissNotification?.(id)}
            />
          )}

          {/* Nuanciers + interrupteur sidebar (mode pastel uniquement), puis
              bascule de thème — commandes partagées avec l'écran de connexion
              (ui/ThemeControls.tsx). */}
          <PaletteSwatches className="hidden sm:flex" />

          <ThemeToggleButton
            theme={theme}
            onToggle={onToggleTheme}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10 text-slate-500 dark:text-slate-400 shader:text-white/70 pastel:text-(--p-ink) pastel:rounded-full pastel:hover:bg-white/70 transition-colors"
          />
        </div>
      </div>
    </header>
  );
}
